import type { Order } from '../../types/orders.types'

/**
 * Status timeline (PE-2167).
 *
 * `GET /orders/:id` returns a `history` array — every status the order has passed through, with an
 * optional note and a timestamp — and none of it was surfaced. The list read does NOT include it,
 * so this renders nothing until the detail is fetched, rather than claiming an order has no history.
 */

const TITLE_GRADIENT: React.CSSProperties = {
  lineHeight: '140%',
  letterSpacing: '0%',
  background:
    'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}

const label = (status: string) =>
  status.replace(/_+/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())

export function StatusHistoryCard({ order }: { order: Order }) {
  // Server order is oldest-first; show newest at the top, which is what a merchant scans for.
  const entries = [...order.history].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  )

  return (
    <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px]">
      <div className="flex items-center gap-[15px] mb-[20px]">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="#6B7280" strokeWidth="2" />
          <path d="M12 7v5l3 2" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h3 className="text-[19px] font-semibold" style={TITLE_GRADIENT}>
          Status History
        </h3>
      </div>

      {entries.length === 0 ? (
        <p className="text-[15px] text-muted-foreground">No status changes recorded yet.</p>
      ) : (
        <ol className="relative border-l border-border ml-[6px]">
          {entries.map((e, i) => (
            <li key={e.id} className="pl-[18px] pb-[16px] last:pb-0 relative">
              <span
                className={`absolute -left-[5px] top-[5px] w-[9px] h-[9px] rounded-full ${
                  i === 0 ? 'bg-foreground' : 'bg-border'
                }`}
              />
              <p className="text-[15px] font-medium text-foreground">{label(e.status)}</p>
              <p className="text-[13px] text-muted-foreground">{new Date(e.timestamp).toLocaleString()}</p>
              {e.note && <p className="text-[13px] text-foreground mt-[2px]">{e.note}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
